import { create } from 'zustand';
import {
  OnboardingFlowNode,
  OnboardingFlowStepNode,
  OnboardingStepType,
  OnboardingFlowNodeType,
  DetailPanelTabs,
  ConditionType,
  FlowValidationError,
  CatchAllCondition,
  StepOutputCondition,
  ConditionValueType,
  IOnboardingFlow,
  OnboardingFlowTransition,
} from '@scale/shared/onboardingControlPanel';
import { CourseVersion } from '@scale/shared/defaults';
import { ReviewLevel } from '@scale/shared-types';
import { AcpActiveUsersData } from 'react-scale/corp/components/QM/training/Util';
import uuid from 'uuid';
import _ from 'lodash';

interface ACPStore {
  selectedNode: OnboardingFlowNode | null;
  setSelectedNode: (node: OnboardingFlowNode | null) => void;
  detailPanelOpen: boolean;
  detailTarget: OnboardingFlowNode | 'Overview' | null;
  setDetailTarget: (target: OnboardingFlowNode | 'Overview' | null) => void;
  setDetailPanelOpen: (open: boolean) => void;
  activeUsersData: AcpActiveUsersData[];
  setActiveUsersData: (data: AcpActiveUsersData[] | undefined) => void;
  detailTargetActiveTab: DetailPanelTabs | null;
  setDetailTargetActiveTab: (tab: DetailPanelTabs | null) => void;
  isLoadingActiveUsersOnStepsData: boolean;
  setIsLoadingActiveUsersOnStepsData: (isLoading: boolean) => void;
  pendingNodes: OnboardingFlowNode[];
  setPendingNodes: (nodes: OnboardingFlowNode[]) => void;
  isDetailTargetEditable: boolean;
  currentReviewLevel: ReviewLevel;
  setCurrentReviewLevel: (level: ReviewLevel) => void;
  isLoadingStepUserData: boolean;
  setIsLoadingStepUserData: (isLoading: boolean) => void;
  highlightedUser: string | null;
  setHighlightedUser: (userId: string | null) => void;
  chosenProjectFilter: boolean;
  setChosenProjectFilter: (chosenProjectFilter: boolean) => void;
  directAssignedFilter: boolean;
  setDirectAssignedFilter: (directAssignedFilter: boolean) => void;
  primaryTeamAssignedFilter: boolean;
  setPrimaryTeamAssignedFilter: (primaryTeamAssignedFilter: boolean) => void;
  secondaryTeamAssignedFilter: boolean;
  setSecondaryTeamAssignedFilter: (secondaryTeamAssignedFilter: boolean) => void;

  // Draft flow store
  currentFlowId: string;
  setCurrentFlowId: (flowId: string) => void;
  isEditing: boolean;
  setIsEditing: (isEditing: boolean) => void;
  validationResults: { isValid: boolean; errors: FlowValidationError[] };
  setValidationResults: (results: { isValid: boolean; errors: FlowValidationError[] }) => void;
  draftFlows: { [key: string]: IOnboardingFlow };
  addDraftFlow: (flow: IOnboardingFlow) => void;
  removeDraftFlow: (flowId: string) => void;
  addDraftNode: (sourceNode: OnboardingFlowNode, isSplit?: boolean) => void;
  addDraftEdge: (sourceNode: string, destNode: string, isPass: boolean) => void;
  getDraftNode: (name: string) => OnboardingFlowNode | undefined;
  editDraftNode: (
    name: string,
    courseId: string,
    newName: string,
    courseVersion: CourseVersion,
  ) => void;
  deleteDraftNode: (name: string) => void;
  deleteDraftEdge: (sourceNode: string, destNode: string) => void;
  moveCatchAllEdgesToBack: () => void;
}

// Util function to determine if the detail target can have edit tab
const _isDetailTargetEditable = (target: OnboardingFlowNode | 'Overview' | null) => {
  return (
    target &&
    target !== 'Overview' &&
    target.type === OnboardingFlowNodeType.Step &&
    (target.stepType === OnboardingStepType.BenchmarkWall ||
      target.stepType === OnboardingStepType.TutorialBenchmark ||
      target.stepType === OnboardingStepType.TaskWall ||
      target.stepType === OnboardingStepType.Training ||
      target.stepType === OnboardingStepType.Course)
  );
};

const useACPStore = create<ACPStore>((set, get) => ({
  selectedNode: null,
  setSelectedNode: node => set({ selectedNode: node }),
  detailPanelOpen: true,
  detailTarget: 'Overview',
  setDetailTarget: target =>
    set({
      detailTarget: target,
      isDetailTargetEditable: _isDetailTargetEditable(target) ?? false,
    }),
  setDetailPanelOpen: open => set({ detailPanelOpen: open }),
  pendingNodes: [],
  setPendingNodes: data => set({ pendingNodes: data }),
  activeUsersData: [],
  setActiveUsersData: data => set({ activeUsersData: data }),
  detailTargetActiveTab: null,
  setDetailTargetActiveTab: tab => set({ detailTargetActiveTab: tab }),
  isLoadingActiveUsersOnStepsData: false,
  setIsLoadingActiveUsersOnStepsData: isLoading =>
    set({ isLoadingActiveUsersOnStepsData: isLoading }),
  isDetailTargetEditable: false,
  currentReviewLevel: ReviewLevel.Attempt,
  setCurrentReviewLevel: level => set({ currentReviewLevel: level }),
  isLoadingStepUserData: false,
  setIsLoadingStepUserData: isLoading => set({ isLoadingStepUserData: isLoading }),
  highlightedUser: null,
  setHighlightedUser: userId => set({ highlightedUser: userId }),
  chosenProjectFilter: false,
  setChosenProjectFilter: chosenProjectFilter => set({ chosenProjectFilter }),
  directAssignedFilter: false,
  setDirectAssignedFilter: directAssignedFilter => set({ directAssignedFilter }),
  primaryTeamAssignedFilter: false,
  setPrimaryTeamAssignedFilter: primaryTeamAssignedFilter => set({ primaryTeamAssignedFilter }),
  secondaryTeamAssignedFilter: false,
  setSecondaryTeamAssignedFilter: secondaryTeamAssignedFilter =>
    set({ secondaryTeamAssignedFilter }),

  // Draft flow store
  currentFlowId: '',
  setCurrentFlowId: flowId => set({ currentFlowId: flowId }),
  isEditing: false,
  setIsEditing: isEditing => set({ isEditing }),
  validationResults: { isValid: true, errors: [] },
  setValidationResults: results => set({ validationResults: results }),
  draftFlows: {},
  addDraftFlow: flow =>
    set({
      draftFlows: {
        ...get().draftFlows,
        [flow._id]: flow,
      },
    }),
  removeDraftFlow: flowId => {
    const draftFlows = get().draftFlows;
    delete draftFlows[flowId];
    set({ draftFlows });
  },
  addDraftNode: (sourceNode: OnboardingFlowNode, isSplit?: boolean) => {
    const newNodeName = `New Course ${uuid.v4().substring(0, 4)}`;
    const flow = get().draftFlows[get().currentFlowId];
    const newNode: OnboardingFlowNode = {
      name: newNodeName,
      type: OnboardingFlowNodeType.Step,
      stepType: OnboardingStepType.Course,
      entryHooks: [],
      exitHooks: [],
      config: {
        type: OnboardingStepType.Course,
        courseId: '',
      },
    };
    flow.nodes = [...flow.nodes, newNode];
    const catchAllCondition: CatchAllCondition[] = [{ type: ConditionType.CatchAll }];
    const courseConditions: StepOutputCondition[] = [
      {
        type: ConditionType.StepOutput,
        value: { type: ConditionValueType.Boolean, value: !isSplit },
      },
    ];
    const edgeName = `${sourceNode.name}->${newNodeName}`;

    if (!isSplit) {
      flow.edges = flow.edges.map(edge => {
        let updatedEdge = { ...edge };

        if (edge.sourceNode === sourceNode.name && edge.destNode !== 'Failed') {
          updatedEdge = {
            ...updatedEdge,
            sourceNode: newNodeName,
            name: `${newNodeName}->${edge.destNode}`,
            conditions:
              updatedEdge.conditions[0].type !== ConditionType.StepOutput
                ? catchAllCondition
                : updatedEdge.conditions,
          };
        }

        return updatedEdge;
      });
    } else {
      // Remove the failed edge from the source node
      flow.edges = flow.edges.filter(
        edge => !(edge.sourceNode === sourceNode.name && edge.destNode === 'Failed'),
      );
    }

    flow.edges = [
      ...flow.edges,
      {
        name: edgeName,
        sourceNode: sourceNode.name,
        destNode: newNodeName,
        conditions: isSplit ? courseConditions : catchAllCondition,
      },
      {
        name: `${newNodeName}->Failed`,
        sourceNode: newNodeName,
        destNode: 'Failed',
        conditions: [
          {
            type: ConditionType.StepOutput,
            value: { type: ConditionValueType.Boolean, value: false },
          },
        ],
      },
    ];
    set({ draftFlows: { ...get().draftFlows, [get().currentFlowId]: flow } });

    get().setSelectedNode(newNode);
    get().setDetailTarget(newNode);
  },
  addDraftEdge: (sourceNode: string, destNode: string, isPass: boolean) => {
    const draftFlows = _.cloneDeep(get().draftFlows);
    const flow = draftFlows[get().currentFlowId];

    const cond = isPass
      ? [
          {
            type: ConditionType.CatchAll,
          },
        ]
      : [
          {
            type: ConditionType.StepOutput,
            value: { type: ConditionValueType.Boolean, value: false },
          },
        ];

    if (flow) {
      const edgeName = `${sourceNode}->${destNode}`;
      const newEdge = {
        name: edgeName,
        sourceNode,
        destNode,
        conditions: cond,
      };

      // @ts-ignore
      flow.edges.push(newEdge);
      set({ draftFlows: { ...draftFlows, [get().currentFlowId]: flow } });
    }
  },
  editDraftNode: (
    name: string,
    courseId: string,
    newName: string,
    courseVersion: CourseVersion,
  ) => {
    const draftFlows = _.cloneDeep(get().draftFlows);
    const flow = draftFlows[get().currentFlowId];

    if (flow) {
      const nodeIndex = flow.nodes.findIndex(node => node.name === name);

      if (nodeIndex !== -1) {
        const updatedNode = {
          ...flow.nodes[nodeIndex],
          name: newName,
          config: {
            ...(flow.nodes[nodeIndex] as OnboardingFlowStepNode).config,
            courseId,
            courseVersion,
          },
        };
        // @ts-ignore
        flow.nodes[nodeIndex] = updatedNode;

        flow.edges = flow.edges.map(edge => {
          let updatedEdge = { ...edge };

          if (edge.sourceNode === name) {
            updatedEdge = {
              ...updatedEdge,
              sourceNode: newName,
              name: `${newName}->${edge.destNode}`,
            };
          }

          if (edge.destNode === name) {
            updatedEdge = {
              ...updatedEdge,
              destNode: newName,
              name: `${edge.sourceNode}->${newName}`,
            };
          }

          return updatedEdge;
        });

        set({ draftFlows: { ...draftFlows, [get().currentReviewLevel]: flow } });
      }
    }
  },
  getDraftNode: (name: string) => {
    const flow = get().draftFlows[get().currentFlowId];
    return flow.nodes.find(node => node.name === name);
  },
  deleteDraftEdge: (sourceNode: string, destNode: string) => {
    const draftFlows = _.cloneDeep(get().draftFlows);
    const flow = draftFlows[get().currentFlowId];

    if (flow) {
      const edgeIndex = flow.edges.findIndex(
        edge => edge.sourceNode === sourceNode && edge.destNode === destNode,
      );

      if (edgeIndex !== -1) {
        flow.edges.splice(edgeIndex, 1);
        set({ draftFlows: { ...draftFlows, [get().currentFlowId]: flow } });
      }
    }
  },
  deleteDraftNode: (name: string) => {
    const draftFlows = _.cloneDeep(get().draftFlows);
    const flow = draftFlows[get().currentFlowId];

    if (flow) {
      const nodeIndex = flow.nodes.findIndex(node => node.name === name);

      if (nodeIndex !== -1) {
        // Find inbound and outbound edges of the node
        const inboundEdges = flow.edges.filter(edge => edge.destNode === name);
        const outboundEdges = flow.edges.filter(edge => edge.sourceNode === name);

        // Check if there is exactly one inbound edge and two outbound edges
        if (
          inboundEdges.length === 1 &&
          outboundEdges.length === 2 &&
          outboundEdges.some(edge => edge.destNode === 'Failed')
        ) {
          // Identify the non-Failed outbound edge
          const incomingEdge = inboundEdges[0];
          const outgoingEdge = outboundEdges.find(edge => edge.destNode !== 'Failed');

          if (outgoingEdge) {
            // Create a new edge from the source of the inbound edge to the target of the non-Failed outbound edge
            const newEdge = {
              ...incomingEdge,
              destNode: outgoingEdge.destNode,
              name: `${incomingEdge.sourceNode}->${outgoingEdge.destNode}`,
            };

            // Check if this edge already exists
            const edgeExists = flow.edges.some(
              edge => edge.sourceNode === newEdge.sourceNode && edge.destNode === newEdge.destNode,
            );

            if (!edgeExists) {
              // Add the new edge to the flow if it's not a duplicate
              flow.edges.push(newEdge);
            }
          }
        }

        // Remove the node and all its edges
        flow.nodes.splice(nodeIndex, 1);
        flow.edges = flow.edges.filter(edge => edge.sourceNode !== name && edge.destNode !== name);

        set({ draftFlows: { ...draftFlows, [get().currentFlowId]: flow } });
      }
    }
  },
  moveCatchAllEdgesToBack: () => {
    const draftFlows = _.cloneDeep(get().draftFlows);
    const flow = draftFlows[get().currentFlowId];

    if (flow) {
      // Separate catchAll edges and other edges
      const catchAllEdges: OnboardingFlowTransition[] = [];
      const otherEdges: OnboardingFlowTransition[] = [];

      flow.edges.forEach(edge => {
        const hasCatchAllCondition = edge.conditions.some(
          condition => condition.type === ConditionType.CatchAll,
        );

        if (hasCatchAllCondition) {
          catchAllEdges.push(edge);
        } else {
          otherEdges.push(edge);
        }
      });

      // Reorder the edges: first non-catchAll edges, then catchAll edges
      flow.edges = [...otherEdges, ...catchAllEdges];

      // Update the store with the modified flow
      set({ draftFlows: { ...draftFlows, [get().currentFlowId]: flow } });
    }
  },
}));

export default useACPStore;
